import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Slim banner that sticks to the top of the viewport when the device goes
 * offline. We intentionally keep it a DOM node (not a toast) so it persists
 * through navigation and reminds the user until the network comes back.
 *
 * Listens to `online`/`offline` window events. Capacitor's Network plugin
 * fires these too on native, so one listener covers all platforms.
 */
export function OfflineBanner() {
  const { t } = useTranslation();
  const [online, setOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );

  useEffect(() => {
    const goOn = () => setOnline(true);
    const goOff = () => setOnline(false);
    window.addEventListener('online', goOn);
    window.addEventListener('offline', goOff);
    return () => {
      window.removeEventListener('online', goOn);
      window.removeEventListener('offline', goOff);
    };
  }, []);

  if (online) return null;

  return (
    <div className="offline-banner" role="status" aria-live="polite">
      <span className="material-symbols-outlined">cloud_off</span>
      <span>{t('common.offline_banner')}</span>
    </div>
  );
}
